import React, { ReactNode, useCallback, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';

const TRAVEL_LEG_S = 12;
const CENTER_PAUSE_S = 10;
const CYCLE_DURATION_S = TRAVEL_LEG_S * 2 + CENTER_PAUSE_S;
const ARRIVE_CENTER_T = TRAVEL_LEG_S / CYCLE_DURATION_S;
const LEAVE_CENTER_T = (TRAVEL_LEG_S + CENTER_PAUSE_S) / CYCLE_DURATION_S;
const DRIFT_TIMES = [0, ARRIVE_CENTER_T, LEAVE_CENTER_T, 1];

interface DriftPositions {
  start: number;
  center: number;
  end: number;
}

interface HeroFloatingGlassCardProps {
  children: ReactNode;
}

const HeroFloatingGlassCard: React.FC<HeroFloatingGlassCardProps> = ({ children }) => {
  const reduceMotion = useReducedMotion();
  const [cardEl, setCardEl] = useState<HTMLDivElement | null>(null);
  const [positions, setPositions] = useState<DriftPositions | null>(null);

  const cardRef = useCallback((node: HTMLDivElement | null) => {
    setCardEl(node);
  }, []);

  const measurePositions = useCallback(() => {
    if (!cardEl) {
      return;
    }

    const width = cardEl.offsetWidth;
    const viewport = window.innerWidth;
    const edgePadding = 24;

    setPositions({
      start: -width - edgePadding,
      center: (viewport - width) / 2,
      end: viewport + edgePadding,
    });
  }, [cardEl]);

  useLayoutEffect(() => {
    measurePositions();
    window.addEventListener('resize', measurePositions);
    return () => window.removeEventListener('resize', measurePositions);
  }, [measurePositions]);

  const driftKeyframes = positions
    ? [positions.start, positions.center, positions.center, positions.end]
    : null;

  const initialX = positions?.start ?? -400;
  const canDrift = Boolean(driftKeyframes) && !reduceMotion;

  return (
    <HeroFloatingLayer>
      <GlassCard
        key={canDrift ? 'hero-drift' : 'hero-measure'}
        ref={cardRef}
        initial={{
          opacity: 0,
          scale: 0.96,
          y: '-50%',
          x: reduceMotion && positions ? positions.center : initialX,
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: '-50%',
          x:
            reduceMotion && positions
              ? positions.center
              : canDrift
                ? driftKeyframes!
                : initialX,
        }}
        transition={{
          opacity: { duration: 0.85, ease: 'easeOut' },
          scale: { duration: 0.85, ease: 'easeOut' },
          y: { duration: 0 },
          x:
            reduceMotion || !canDrift
              ? { duration: 0 }
              : {
                  duration: CYCLE_DURATION_S,
                  times: DRIFT_TIMES,
                  ease: ['easeInOut', 'linear', 'linear', 'easeInOut'],
                  repeat: Infinity,
                  repeatDelay: 2,
                },
        }}
      >
        <GlassSheen aria-hidden />
        {children}
      </GlassCard>
    </HeroFloatingLayer>
  );
};

const HeroFloatingLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
`;

const GlassCard = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 0;
  pointer-events: auto;
  width: min(540px, calc(100vw - 40px));
  padding: clamp(28px, 4.5vw, 44px) clamp(24px, 4vw, 40px);
  text-align: center;
  color: #fff;
  will-change: transform;

  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.28) 0%,
    rgba(255, 255, 255, 0.1) 45%,
    rgba(255, 255, 255, 0.06) 100%
  );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-radius: 28px;
  box-shadow:
    0 28px 80px rgba(15, 23, 42, 0.35),
    0 12px 32px rgba(15, 23, 42, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -1px 0 rgba(255, 255, 255, 0.1);

  @media (max-width: 640px) {
    border-radius: 22px;
    width: min(400px, calc(100vw - 32px));
  }
`;

const GlassSheen = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(
    125deg,
    rgba(255, 255, 255, 0.35) 0%,
    transparent 42%,
    transparent 58%,
    rgba(255, 255, 255, 0.08) 100%
  );
  opacity: 0.7;
`;

export default HeroFloatingGlassCard;
